/**
 * @fileoverview Renovation Projects Table Configuration
 *
 * Centralized configuration for the renovation projects table.
 * Includes column definitions, sort logic, and picker configurations.
 *
 * @module RenovationProjects/TableConfig
 */

import {
  TableColumnsConfig,
  TableSortConfig,
} from "shared/components/UniversalTable/types";

// ========== COLUMN CONFIGURATION ==========

/**
 * Renovation projects table column definitions
 */
export const RENOVATION_PROJECTS_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: "code",
      label: "Code",
      minWidth: 100,
      defaultWeight: 0, // Fixed width
      sortKey: "id",
    },
    {
      id: "title",
      label: "Titel",
      minWidth: 200,
      defaultWeight: 6, // Main content column
      sortKey: "title",
    },
    {
      id: "building",
      label: "Gebouw",
      minWidth: 120,
      defaultWeight: 3,
      sortKey: "building",
    },
    {
      id: "company",
      label: "Vennootschap",
      minWidth: 150,
      defaultWeight: 3,
      sortKey: "company",
      visible: "lg",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 130,
      defaultWeight: 2,
      sortKey: "status",
    },
    {
      id: "parts",
      label: "Delen",
      minWidth: 70,
      defaultWeight: 0, // Fixed width
      className: "flex justify-center",
    },
    {
      id: "date",
      label: "Aangemaakt",
      minWidth: 100,
      defaultWeight: 0, // Fixed width
      sortKey: "createdAt",
      visible: "lg",
    },
    {
      id: "actions",
      label: "",
      minWidth: 50,
      defaultWeight: 0, // Fixed width
      className: "flex justify-end",
    },
  ],
  localStorageKey: "renovationProjectsColumnWidths",
};

// ========== SORT CONFIGURATION ==========

/**
 * Renovation projects table sort configuration
 */
export const RENOVATION_PROJECTS_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: "createdAt",
  defaultDirection: "desc", // Most recent first

  fields: [
    // ID (code) - numeric sorting
    {
      key: "id",
      extractor: (row) => row.id || 0,
    },
    // Title - alphabetical
    {
      key: "title",
      extractor: (row) => (row.title || "").toLowerCase(),
    },
    // Building - by building ID
    {
      key: "building",
      extractor: (row) => row.building?.id || "",
    },
    // Company - by company name
    {
      key: "company",
      extractor: (row) => row.building?.company?.name?.toLowerCase() || "",
    },
    // Status - by workflow order
    {
      key: "status",
      extractor: (row) => {
        const statusOrder: Record<string, number> = {
          "Ontwerp fase": 5,
          Gepland: 4,
          "In uitvoering": 3,
          Afgerond: 2,
          "Volledig gefactureerd": 1,
        };
        return statusOrder[row.status] || 0;
      },
    },
    // Date - by creation date
    {
      key: "createdAt",
      extractor: (row) => new Date(row.createdAt || "").getTime(),
    },
  ],

  caseSensitive: false,
};

// ========== FILTER SECTIONS ==========

/**
 * Filter sections for the FilterBar
 */
export const RENOVATION_PROJECTS_FILTER_SECTIONS = [
  {
    key: "status",
    label: "Status",
    options: [
      { key: "Ontwerp fase", label: "Ontwerp" },
      { key: "Gepland", label: "Gepland" },
      { key: "In uitvoering", label: "In uitvoering" },
      { key: "Afgerond", label: "Afgerond" },
      { key: "Volledig gefactureerd", label: "Gefactureerd" },
    ],
    color: "moss" as const,
  },
];

// ========== COMBINED CONFIGURATION ==========

/**
 * Complete renovation projects table configuration
 */
export const RENOVATION_PROJECTS_TABLE_CONFIG = {
  columnsConfig: RENOVATION_PROJECTS_COLUMNS_CONFIG,
  sortConfig: RENOVATION_PROJECTS_SORT_CONFIG,
};
